import React, { useContext, useEffect, useState } from 'react'
import dayjs from 'dayjs'
import { t } from '../i18n/i18n'
import { Layout } from './Layout'
import { DataContext } from '../context/DataContext'
import { Activity, FinancialYear, RiskAnalysis, Workplace } from '../api/schemas/schema'
import { DashboardModules } from '../components/dashboard/DashboardModules'
import { IComplianceState } from '../interfaces/IComplianceState'
import { IListState } from '../interfaces/IListState'
import { ModalActivity } from '../components/calendar/ModalActivity'
import { ModalDemandAssessment } from '../components/compliance/ModalDemandAssessment'
import { ModalRiskAssesment } from '../components/riskAnalyses/ModalRiskAssesment'
import { getComplianceRelatedProperties } from '../helpers/demandAssessmentHelper'
import { getAllWorkplaces, getLatestActivities } from '../helpers/dashboardHelper'
import { NavigationLink } from '../interfaces/INavigationLink'
import { ModalAddFinancialYear } from '../components/riskEconomy/ModalAddFinancialYear'

export const Dashboard = () => {
   const dataContext = useContext(DataContext)
   const { organisation } = dataContext.state
   const [latestActivities, setLatestActivities] = useState<Activity[] | undefined>(undefined)

   const [workplaces, setWorkplaces] = useState<Workplace[]>([])

   const [complianceState, setComplianceState] = useState<IComplianceState>({
      loading: true,
      allDemandAssessments: [],
      filteredDemandAssessments: [],
      open: false,
      fetchDone: false,
      leadershipPerspectives: [],
      processes: [],
      demandComplianceModules: [],
      subjects: [],
      demandAnalysisStatuses: [],
   })

   const [riskManagementState, setRiskManagementState] = useState<IListState<RiskAnalysis>>({
      loading: true,
      filteredItems: [],
      allItems: [],
      open: false,
   })

   const [activityModalState, setActivityModalState] = useState<{ isOpen: boolean; selectedActivity?: Activity }>({
      isOpen: false,
      selectedActivity: undefined,
   })

   const [riskEconomyState, setRiskEconomyState] = React.useState<IListState<FinancialYear>>({
      loading: true,
      open: false,
      allItems: [],
      filteredItems: [],
   })

   useEffect(() => {
      if (!organisation?.id) return
      const ac = new AbortController()
      Promise.all([
         getLatestActivities(dataContext, setLatestActivities, ac.signal),
         getAllWorkplaces(dataContext, setWorkplaces, ac.signal),
      ]).finally(() => { if (!ac.signal.aborted) setLatestActivities((prev) => prev ?? []) })
      return () => ac.abort()
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [organisation?.id])

   useEffect(() => {
      if (complianceState.open && !complianceState.fetchDone) {
         getComplianceRelatedProperties(dataContext, setComplianceState)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [complianceState.open])

   return (
      <Layout breadCrumbItems={[{ title: t('Overview'), active: true, path: '/' }]} activeDesktopLink={NavigationLink.Overview} overflowAuto>
         <DashboardModules
            setComplianceState={setComplianceState}
            setActivityModalState={setActivityModalState}
            setRiskManagementState={setRiskManagementState}
            setRiskEconomyState={setRiskEconomyState}
            latestActivities={latestActivities}
         />
         <ModalDemandAssessment complianceState={complianceState} setComplianceState={setComplianceState} workplaces={workplaces} />
         <ModalActivity modalState={activityModalState} setActivities={setLatestActivities} setModalState={setActivityModalState} currentYear={dayjs().year()} />
         <ModalRiskAssesment riskManagementState={riskManagementState} setRiskManagementState={setRiskManagementState} workplaces={workplaces} />
         <ModalAddFinancialYear riskEconomyState={riskEconomyState} setRiskEconomyState={setRiskEconomyState} workplaces={workplaces} />
      </Layout>
   )
}
